import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Btn, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlayCircle, RefreshCw, Lock, History as HistoryIcon } from "lucide-react";

function defaultSql(month) {
  const [y, m] = month.split("-").map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const end = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")} 23:59:59`;
  return `SELECT Provider, brandId,
  SUM(OrderCount) AS TotalOrders,
  SUM(Winloss) AS TotalCustomerWinLoss,
  SUM(TurnOver) AS TotalTurnover
FROM gg_dashboard.brands_provider_behaviour
WHERE Date >= '${start}' AND Date <= '${end}'
GROUP BY brandId, Provider
ORDER BY brandId`;
}

function formatTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function MonthlyRunPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [sql, setSql] = useState(defaultSql(month));
  const [showSql, setShowSql] = useState(false);
  const [rows, setRows] = useState([]);
  const [snapshot, setSnapshot] = useState(null); // { ran_at, by, row_count, version, locked }
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [history, setHistory] = useState([]);
  const [brands, setBrands] = useState([]);
  const [providers, setProviders] = useState([]);
  const [fxRates, setFxRates] = useState({});  // { INR: 0.012, PKR: 0.0035, USD: 1, ... } for current month

  useEffect(() => { setSql(defaultSql(month)); }, [month]);
  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/providers").then((r) => setProviders(r.data));
  }, []);
  useEffect(() => {
    // FX rates for the selected month → convert per-brand-currency values to USD
    api.get(`/fx-rates?month=${month}`).then((r) => {
      const m = { USD: 1 };
      (r.data || []).forEach((f) => { m[(f.currency || "").toUpperCase()] = Number(f.rate_to_usd) || 0; });
      setFxRates(m);
    }).catch(() => setFxRates({ USD: 1 }));
  }, [month]);
  const toUsd = (amount, currency) => {
    const cur = (currency || "USD").toUpperCase();
    const rate = fxRates[cur] ?? (cur === "USD" ? 1 : 0);
    return Number(amount || 0) * rate;
  };

  // Lookup maps for enrichment
  const brandsByExtId = brands.reduce((acc, b) => { if (b.brand_id) acc[String(b.brand_id)] = b; return acc; }, {});
  const providersByCode = providers.reduce((acc, p) => { acc[(p.code || "").toLowerCase()] = p; return acc; }, {});

  const loadSnapshot = async () => {
    try {
      const { data } = await api.get(`/ggr/runs/${month}`);
      setRows(data.rows || []);
      setSnapshot({
        ran_at: data.ran_at, by: data.by, row_count: data.row_count,
        version: data.version || 1, locked: data.locked !== false,
      });
    } catch {
      setRows([]); setSnapshot(null);
    }
    try {
      const { data } = await api.get(`/ggr/runs/${month}/history`);
      setHistory(data || []);
    } catch { setHistory([]); }
  };

  useEffect(() => {
    api.get("/ggr/runs").then((r) => setRuns(r.data));
    loadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const doRun = async (force) => {
    setRunning(true);
    try {
      const body = { month, force: !!force };
      if (showSql) body.custom_sql = sql;
      const { data } = await api.post("/ggr/run", body);
      setRows(data.rows);
      setSnapshot({ ran_at: data.ran_at, by: data.by, row_count: data.count, version: data.version, locked: true });
      toast.success(force ? `Reloaded ${month} · v${data.version} · ${data.count} rows` : `Ran ${month} · ${data.count} rows`);
      const r2 = await api.get("/ggr/runs"); setRuns(r2.data);
      loadSnapshot();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Snapshot exists — use Reload to overwrite.");
      } else {
        toast.error(fmtError(e));
      }
    } finally { setRunning(false); }
  };

  const onRunClick = () => {
    if (snapshot) {
      const ok = window.confirm(
        `A locked snapshot for ${month} already exists (v${snapshot.version}, ${snapshot.row_count} rows, run on ${formatTs(snapshot.ran_at)} by ${snapshot.by}).\n\nReloading will archive the current snapshot to history and replace it with fresh data from MySQL.\n\nContinue?`
      );
      if (!ok) return;
      doRun(true);
    } else {
      doRun(false);
    }
  };

  const fmt = (v, digits = 2) => v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

  return (
    <AppShell
      title="Monthly GGR Run"
      subtitle="Executes the aggregation query on the remote DB through the SSH jumphost"
      actions={
        <div className="flex gap-2 items-center">
          <input data-testid="run-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 rounded-sm bg-white border border-[var(--wv-border)] font-mono text-sm"/>
          {isAdmin && (
            <Btn data-testid="run-execute" onClick={onRunClick} disabled={running}>
              {snapshot
                ? <><RefreshCw size={16} className="inline mr-1"/>{running ? "Reloading..." : "Reload from MySQL"}</>
                : <><PlayCircle size={16} className="inline mr-1"/>{running ? "Running..." : "Run"}</>}
            </Btn>
          )}
        </div>
      }
    >
      {snapshot && (
        <div className="wv-card p-5 mb-6" data-testid="snapshot-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-9 w-9 rounded-xl bg-[var(--wv-accent-light)] flex items-center justify-center text-[var(--wv-primary)]">
                <Lock size={16} strokeWidth={1.8}/>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-medium text-[var(--wv-text)]">Snapshot locked</div>
                  <Badge tone="success">v{snapshot.version}</Badge>
                </div>
                <div className="text-sm text-[var(--wv-text-2)] mt-0.5">
                  {snapshot.row_count} rows · ran on <span className="font-mono">{formatTs(snapshot.ran_at)}</span> by <span className="font-mono">{snapshot.by}</span>
                </div>
                <div className="text-xs text-[var(--wv-text-3)] mt-1">
                  Numbers are frozen. Tally, invoices and reports read from this snapshot. Use "Reload from MySQL" to fetch fresh data (previous version will be archived to history).
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="wv-card p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="wv-overline">Query</div>
              <div className="text-sm text-[var(--wv-text-2)] mt-1">The default aggregation query for the selected month is applied automatically. Toggle to edit before running.</div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[var(--wv-text-2)]">
              <input type="checkbox" checked={showSql} onChange={(e) => setShowSql(e.target.checked)} data-testid="run-toggle-sql"/>
              Customize SQL
            </label>
          </div>
          {showSql && (
            <textarea data-testid="run-sql" value={sql} onChange={(e) => setSql(e.target.value)} rows={10} className="wv-sql-editor mt-4"/>
          )}
        </div>
      )}

      {rows.length > 0 && (() => {
        // Group by Provider Head (head_name or aggregator or provider itself).
        // All monetary fields converted to USD using month FX so cross-currency sums are meaningful.
        const groups = {};
        const currenciesSeen = new Set();
        const missingFx = new Set();
        rows.forEach((r) => {
          const pInfo = providersByCode[(r.provider || "").toLowerCase()];
          const headDisplay = pInfo?.head_name || r.provider || "—";
          const key = headDisplay.toLowerCase();
          const cur = (r.brand_currency || "USD").toUpperCase();
          currenciesSeen.add(cur);
          const rate = fxRates[cur];
          if (rate === undefined || rate === 0) if (cur !== "USD") missingFx.add(cur);
          if (!groups[key]) groups[key] = { head: headDisplay, provider_codes: new Set(), brands: 0, orders: 0, turnover_usd: 0, ggr_usd: 0, share_usd: 0 };
          groups[key].provider_codes.add(r.provider);
          groups[key].brands += 1;
          groups[key].orders += Number(r.orders || 0);
          groups[key].turnover_usd += toUsd(r.turnover, cur);
          groups[key].ggr_usd += toUsd(r.ggr, cur);
          groups[key].share_usd += toUsd(r.provider_share, cur);
        });
        const arr = Object.values(groups).sort((a, b) => b.ggr_usd - a.ggr_usd);
        const showFxWarning = missingFx.size > 0;
        return (
          <div className="wv-card p-6 mb-6" data-testid="run-summary-by-head">
            <div className="flex items-center justify-between mb-3">
              <div className="wv-overline">Summary by Provider Head · all amounts in USD</div>
              <div className="text-xs text-[var(--wv-text-3)] font-mono">
                {Array.from(currenciesSeen).sort().join(" · ")} → USD (FX for {month})
              </div>
            </div>
            {showFxWarning && (
              <div className="mb-3 p-3 rounded-md bg-[var(--wv-warning)]/10 border border-[var(--wv-warning)]/30 text-sm text-[var(--wv-text-2)]" data-testid="run-summary-fx-warning">
                Missing FX rate for {month}: <span className="font-mono">{Array.from(missingFx).join(", ")}</span>. Rows in those currencies contribute $0 to the totals below. Add rates under Settings → FX Rates.
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[var(--wv-text-3)] text-xs uppercase tracking-widest">
                  <th className="text-left py-1 pr-3">Provider Head</th>
                  <th className="text-left py-1 pr-3">Providers</th>
                  <th className="text-right py-1 pr-3">Brand rows</th>
                  <th className="text-right py-1 pr-3">Orders</th>
                  <th className="text-right py-1 pr-3">Turnover (USD)</th>
                  <th className="text-right py-1 pr-3">GGR (USD)</th>
                  <th className="text-right py-1">Provider Share (USD)</th>
                </tr>
              </thead>
              <tbody>
                {arr.map((g) => (
                  <tr key={g.head.toLowerCase()} className="border-t border-white/50">
                    <td className="py-1.5 pr-3 font-medium">{g.head}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-[var(--wv-text-2)]">{Array.from(g.provider_codes).join(", ")}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{g.brands}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{fmt(g.orders, 0)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">${fmt(g.turnover_usd)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-medium">${fmt(g.ggr_usd)}</td>
                    <td className="py-1.5 text-right font-mono font-medium text-[var(--wv-primary)]">${fmt(g.share_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      <DataTable
        testid="run-results-table"
        columns={[
          { key: "provider", label: "Provider", mono: true },
          { key: "head_name", label: "Provider Head", mono: true, render: (r) => {
            const p = providersByCode[(r.provider || "").toLowerCase()];
            return p?.head_name || <span className="text-[var(--wv-text-3)]">—</span>;
          } },
          { key: "brand_id", label: "Brand", render: (r) => {
            const b = brandsByExtId[String(r.brand_id)];
            if (b) return <span><span className="font-medium">{b.name}</span> <span className="font-mono text-xs text-[var(--wv-text-3)]">({r.brand_id})</span></span>;
            return <span className="font-mono">{r.brand_id ?? "—"}</span>;
          } },
          { key: "brand_currency", label: "Currency", mono: true, render: (r) => (
            <Badge tone={r.brand_currency === "USD" ? "success" : "info"}>{r.brand_currency || "—"}</Badge>
          ) },
          { key: "orders", label: "Orders", mono: true, render: (r) => fmt(r.orders, 0) },
          { key: "turnover", label: "Turnover", mono: true, render: (r) => <span>{fmt(r.turnover)} <span className="text-xs text-[var(--wv-text-3)]">{r.brand_currency}</span></span> },
          { key: "customer_winloss", label: "Customer WinLoss", mono: true, render: (r) => <span>{fmt(r.customer_winloss)} <span className="text-xs text-[var(--wv-text-3)]">{r.brand_currency}</span></span> },
          { key: "ggr", label: "GGR", mono: true, render: (r) => <span className="font-medium">{fmt(r.ggr)} <span className="text-xs text-[var(--wv-text-3)] font-normal">{r.brand_currency}</span></span> },
          { key: "ggr_pct", label: "GGR %", mono: true, render: (r) => r.ggr_pct != null ? `${Number(r.ggr_pct).toFixed(2)}%` : <Badge tone="warning">no provider</Badge> },
          { key: "provider_share", label: "Provider Share", mono: true, render: (r) => <span className="font-medium">{fmt(r.provider_share)} <span className="text-xs text-[var(--wv-text-3)] font-normal">{r.brand_currency}</span></span> },
          { key: "share_usd", label: "Share (USD)", mono: true, render: (r) => {
            const usd = toUsd(r.provider_share, r.brand_currency);
            const cur = (r.brand_currency || "USD").toUpperCase();
            if (cur !== "USD" && (fxRates[cur] === undefined || fxRates[cur] === 0)) {
              return <span className="text-[var(--wv-warning)] text-xs">no FX rate</span>;
            }
            return <span className="text-[var(--wv-primary)]">${fmt(usd)}</span>;
          } },
        ]}
        rows={rows}
      />

      {history.length > 0 && (
        <div className="wv-card p-6 mt-6" data-testid="snapshot-history">
          <div className="flex items-center gap-2">
            <HistoryIcon size={16} className="text-[var(--wv-text-2)]"/>
            <div className="wv-overline">Snapshot history · {month}</div>
          </div>
          <div className="mt-3 divide-y divide-white/50">
            {history.map((h) => (
              <div key={h.id} className="py-2 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Badge>v{h.version || 1}</Badge>
                  <span className="text-[var(--wv-text-2)]">{h.row_count} rows</span>
                  <span className="text-[var(--wv-text-3)]">·</span>
                  <span className="font-mono text-[var(--wv-text-3)]">ran {formatTs(h.ran_at)}</span>
                  <span className="text-[var(--wv-text-3)]">by</span>
                  <span className="font-mono text-[var(--wv-text-3)]">{h.by}</span>
                </div>
                <div className="text-xs text-[var(--wv-text-3)] font-mono">archived {formatTs(h.archived_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="wv-card p-6 mt-6">
        <div className="wv-overline">Other months with snapshots</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {runs.map((r) => (
            <button key={r.id || r.month} onClick={() => setMonth(r.month)} className="px-3 py-1.5 text-sm rounded-sm bg-[var(--wv-surface-alt)] hover:bg-[var(--wv-accent-light)] font-mono">
              {r.month} · {r.row_count} rows{r.version ? ` · v${r.version}` : ""}
            </button>
          ))}
          {runs.length === 0 && <div className="text-sm text-[var(--wv-text-3)]">No historic runs yet.</div>}
        </div>
      </div>
    </AppShell>
  );
}
